import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueuedSearchParams } from 'NavigationHelpers';
import { type BrowseFilters, type ProgressStatus, type StatusOption } from '../BrowseCourses.types';
import { EMPTY_FILTERS, sanitizeSearchText } from '../filterStreams';
import { filterChanges, loadSavedFilters, saveFilters } from '../filterPersistence';

// Save the restored filters on load, so the URL shows them
function useCommitOnce(commit: (filters: BrowseFilters) => void, initialFilters: BrowseFilters) {
    const done = useRef(false);
    useEffect(() => {
        if (done.current) return;
        done.current = true;
        commit(initialFilters);
    }, [commit, initialFilters]);
}

/*
    `filters` drive the results. `draft` is what the filter controls show. They are the same except while
    the mobile filter drawer is open: then changes stay in the draft until the user saves, and closing
    the drawer without saving restores the committed filters. Saved filters resolve on mount against
    `sources`: the topics and status options of the loaded streams.
*/
export function useBrowseCoursesFilters(sources: Parameters<typeof loadSavedFilters>[1]) {
    const { searchParams, queueSearchParamsUpdate } = useQueuedSearchParams();
    const [initialFilters] = useState(() => loadSavedFilters(searchParams, sources));
    const [filters, setFilters] = useState(initialFilters);
    const [draft, setDraft] = useState(initialFilters);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const commit = useCallback(
        (next: BrowseFilters) => {
            const committed = { ...next, searchText: sanitizeSearchText(next.searchText) };
            setFilters(committed);
            setDraft(committed);
            saveFilters(committed, queueSearchParamsUpdate);
        },
        [queueSearchParamsUpdate],
    );

    useCommitOnce(commit, initialFilters);

    // Applies a change to the draft, and commits it unless the mobile drawer is open
    const update = (next: BrowseFilters) => (drawerOpen ? setDraft(next) : commit(next));

    return {
        filters,
        draft,
        drawerOpen,
        clearTopics: () => update(filterChanges.clearTopics(draft)),
        toggleTopic: (topic: string) => update(filterChanges.toggleTopic(draft, topic)),
        clearProgress: () => update(filterChanges.clearProgress(draft)),
        toggleProgress: (progress: ProgressStatus) => update(filterChanges.toggleProgress(draft, progress)),
        toggleStatus: (status: StatusOption) => update(filterChanges.toggleStatus(draft, status)),
        // The search input is outside the drawer, so its text always commits. An open drawer keeps its draft.
        setSearchText: (searchText: string) => {
            commit(filterChanges.setSearchText(filters, searchText));
            if (drawerOpen) setDraft(filterChanges.setSearchText(draft, sanitizeSearchText(searchText)));
        },
        // Reset on the no-results message clears everything and commits at once
        clearAll: () => commit(EMPTY_FILTERS),
        openDrawer: () => setDrawerOpen(true),
        saveDrawer: () => {
            setDrawerOpen(false);
            commit(draft);
        },
        // The drawer can report a close after a save closed it. Only an open drawer has changes to discard.
        cancelDrawer: () => {
            if (!drawerOpen) return;
            setDrawerOpen(false);
            setDraft(filters);
        },
    };
}
